import { Scene } from 'phaser';
import { AccidentalGlyphGraphic, ACCIDENTAL_RENDERING, type AccidentalSymbol } from './Note';
import type { ClefType } from './Types';

const TREBLE_SHARP_LINES = [0, 3, -1, 2, 5, 1, 4];
const TREBLE_FLAT_LINES = [4, 1, 5, 2, 6, 3, 7];
const BASS_SHARP_LINES = [2, 5, 1, 4, 7, 3, 6];
const BASS_FLAT_LINES = [6, 3, 7, 4, 8, 5, 9];
// Center-to-center step between adjacent key-signature accidentals, as a fraction of
// the glyph width - accidentals nest slightly, matching standard engraving.
const GLYPH_ADVANCE_FACTOR = 0.92;

export const getKeySignatureWidth = (
  fifths: number,
  pitchLineGap: number,
  dpr: number = 1,
): number => {
  const count = Math.abs(fifths);
  if (count === 0) return 0;
  const symbol: AccidentalSymbol = fifths > 0 ? '#' : 'b';
  const glyphWidth = pitchLineGap * dpr * ACCIDENTAL_RENDERING[symbol].widthInStaffSpaces;
  const step = glyphWidth * GLYPH_ADVANCE_FACTOR;
  return glyphWidth + Math.max(0, count - 1) * step;
};

export default class KeySignatureGraphic extends Phaser.GameObjects.Container {
  private readonly glyphs: AccidentalGlyphGraphic[];
  private readonly symbol: AccidentalSymbol;

  constructor(
    scene: Scene,
    private readonly clef: ClefType,
    private readonly fifths: number,
    private notationColor: number,
  ) {
    super(scene, 0, 0);
    this.symbol = fifths > 0 ? '#' : 'b';
    this.glyphs = Array.from(
      { length: Math.abs(fifths) },
      () => new AccidentalGlyphGraphic(scene, this.symbol, notationColor),
    );
    this.add(this.glyphs);
  }

  resize(pitchLineGap: number, dpr: number) {
    const metrics = ACCIDENTAL_RENDERING[this.symbol];
    const height = pitchLineGap * dpr * metrics.heightInStaffSpaces;
    const glyphWidth = pitchLineGap * dpr * metrics.widthInStaffSpaces;
    const step = glyphWidth * GLYPH_ADVANCE_FACTOR;
    const linePositions = this.getLinePositions();

    this.glyphs.forEach((glyph, index) => {
      glyph.resizeToHeight(height);
      glyph.setPosition(
        glyphWidth / 2 + index * step,
        // The SVG bakes the SMuFL registration point at the glyph's vertical center, so this
        // pitch-line position sits the accidental on the line/space of the note it alters.
        (linePositions[index] / 2) * pitchLineGap * dpr,
      );
    });
    this.setSize(getKeySignatureWidth(this.fifths, pitchLineGap, dpr), pitchLineGap * dpr * 4);
  }

  setNotationColor(color: number) {
    this.notationColor = color;
    this.glyphs.forEach((glyph) => glyph.setNotationColor(this.notationColor));
  }

  private getLinePositions(): number[] {
    if (this.clef === 'treble') {
      return this.symbol === '#' ? TREBLE_SHARP_LINES : TREBLE_FLAT_LINES;
    }
    return this.symbol === '#' ? BASS_SHARP_LINES : BASS_FLAT_LINES;
  }

  destroy(fromScene?: boolean) {
    super.destroy(fromScene);
  }
}
